import { onViewLoad } from "~/jose_modern/on_view_load.js";
import { once } from "~/jose_modern/once.js";
import { Flash } from "~/jose_modern/flash";
import { updateRecipeCardSettingsRecipePath, updateImageScaleRecipePath } from "routes";

function saveSettings(settings) {
  const recipeId = document.getElementById("recipe-card-sections").dataset.recipeId;
  fetch(updateRecipeCardSettingsRecipePath(recipeId), {
    method: "PATCH",
    headers: { "Content-Type": "application/json", "Accept": "application/json" },
    body: JSON.stringify({ recipe_card_settings: settings }),
  })
    .then((r) => {
      if (!r.ok) Flash.error("Failed to save card settings.");
    })
    .catch(() => Flash.error("Failed to save card settings."));
}

function currentOrder() {
  return Array.from(document.querySelectorAll(".recipe-card-section")).map(
    (el) => el.dataset.section
  );
}

function updateArrowVisibility() {
  const sections = document.querySelectorAll(".recipe-card-section");
  sections.forEach((section, i) => {
    section.querySelector(".recipe-card-move-up").style.visibility = i === 0 ? "hidden" : "";
    section.querySelector(".recipe-card-move-down").style.visibility =
      i === sections.length - 1 ? "hidden" : "";
  });
}

function bindReordering() {
  let animating = false;

  document.querySelectorAll(".recipe-card-move-up, .recipe-card-move-down").forEach((btn) => {
    btn.addEventListener("click", () => {
      if (animating) return;

      const section = btn.closest(".recipe-card-section");
      const parent = section.parentElement;
      const isUp = btn.classList.contains("recipe-card-move-up");
      const sibling = isUp ? section.previousElementSibling : section.nextElementSibling;
      if (!sibling) return;

      // Do the DOM swap first, then animate from old positions
      const sectionRect = section.getBoundingClientRect();
      const siblingRect = sibling.getBoundingClientRect();

      if (isUp) {
        parent.insertBefore(section, sibling);
      } else {
        parent.insertBefore(sibling, section);
      }

      // Calculate how far each moved and animate back from there
      const sectionNewRect = section.getBoundingClientRect();
      const siblingNewRect = sibling.getBoundingClientRect();

      const sectionDelta = sectionRect.top - sectionNewRect.top;
      const siblingDelta = siblingRect.top - siblingNewRect.top;

      section.style.transition = "none";
      sibling.style.transition = "none";
      section.style.transform = `translateY(${sectionDelta}px)`;
      sibling.style.transform = `translateY(${siblingDelta}px)`;

      // Force reflow so the browser registers the initial offset
      section.offsetHeight;

      animating = true;

      const transition = `transform 250ms ease`;
      section.style.transition = transition;
      sibling.style.transition = transition;
      section.style.transform = "";
      sibling.style.transform = "";

      setTimeout(() => {
        animating = false;
      }, 250);

      updateArrowVisibility();
      saveSettings({ section_order: currentOrder() });
    });
  });

  updateArrowVisibility();
}

function currentHidden() {
  return Array.from(document.querySelectorAll(".recipe-card-section--hidden")).map(
    (el) => el.dataset.section
  );
}

function bindHideToggles() {
  document.querySelectorAll(".recipe-card-hide-toggle").forEach((checkbox) => {
    checkbox.addEventListener("change", () => {
      const section = checkbox.closest(".recipe-card-section");
      section.classList.toggle("recipe-card-section--hidden", checkbox.checked);
      saveSettings({ hidden: currentHidden() });
    });
  });
}

function applyScale(level) {
  document.getElementById("recipe-card-sections").dataset.scale = level;
  document.getElementById("recipe-card-scale-down").style.visibility = level <= 1 ? "hidden" : "";
  document.getElementById("recipe-card-scale-up").style.visibility = level >= 5 ? "hidden" : "";
}

function bindScaleButtons() {
  const container = document.getElementById("recipe-card-sections");

  document.getElementById("recipe-card-scale-down").addEventListener("click", () => {
    const level = parseInt(container.dataset.scale) - 1;
    if (level < 1) return;
    applyScale(level);
    saveSettings({ scale: level });
  });

  document.getElementById("recipe-card-scale-up").addEventListener("click", () => {
    const level = parseInt(container.dataset.scale) + 1;
    if (level > 5) return;
    applyScale(level);
    saveSettings({ scale: level });
  });

  applyScale(parseInt(container.dataset.scale));
}

function bindImageScaleSliders() {
  document.querySelectorAll(".recipe-card-image-wrapper").forEach((wrapper) => {
    const slider = wrapper.querySelector(".recipe-card-image-scale input[type=range]");
    const container = wrapper.querySelector(".recipe-card-image");
    const valueLabel = wrapper.querySelector(".recipe-card-image-scale-value");

    slider.addEventListener("input", () => {
      container.style.width = `${slider.value}%`;
      valueLabel.textContent = `${slider.value}%`;
    });

    slider.addEventListener("change", () => {
      const recipeId = slider.dataset.recipeId;
      const attachmentId = slider.dataset.attachmentId;
      const scale = slider.value / 100;

      fetch(updateImageScaleRecipePath(recipeId), {
        method: "PUT",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({ attachment_id: attachmentId, scale }),
      })
        .then((r) => {
          if (!r.ok) Flash.error("Failed to save image scale.");
        })
        .catch(() => Flash.error("Failed to save image scale."));
    });
  });
}

function bindScaleRecipeForm() {
  const select = document.querySelector("[data-recipe-card-scale-mode]");
  const valueInput = document.querySelector("[data-recipe-card-scale-value]");
  if (!select || !valueInput) return;

  const ingredientSelect = document.querySelector("[data-recipe-card-scale-ingredient]");
  const unitSelect = document.querySelector("[data-recipe-card-scale-unit]");
  const ingredientControls = document.querySelectorAll(
    "[data-recipe-card-scale-ingredient-control], [data-recipe-card-scale-unit-control]"
  );

  const placeholders = {
    factor: "e.g. 2.5",
    packages: "e.g. 500",
    servings: "e.g. 6",
    ingredient: "e.g. 5",
  };

  function applyPlaceholder() {
    valueInput.placeholder = placeholders[select.value];
  }

  function applyIngredientVisibility() {
    ingredientControls.forEach((control) => {
      control.classList.toggle("is-hidden", select.value !== "ingredient");
    });
  }

  function applyUnitOptions(preferredUnit) {
    const option = ingredientSelect.options[ingredientSelect.selectedIndex];
    const units = JSON.parse(option.dataset.units);

    unitSelect.innerHTML = "";
    units.forEach((unit) => unitSelect.add(new Option(unit, unit)));
    unitSelect.value = units.includes(preferredUnit) ? preferredUnit : option.dataset.defaultUnit;
  }

  select.addEventListener("change", () => {
    applyPlaceholder();
    applyIngredientVisibility();
  });

  if (ingredientSelect && unitSelect) {
    ingredientSelect.addEventListener("change", () => applyUnitOptions());
    applyUnitOptions(unitSelect.value);
  }

  applyPlaceholder();
  applyIngredientVisibility();
}

function bindImageDropzone() {
  const dropzone = document.querySelector("[data-dropzone]");
  const input = dropzone.querySelector("[data-dropzone-input]");
  const form = dropzone.closest("form");

  dropzone.addEventListener("click", () => input.click());

  dropzone.addEventListener("dragover", (e) => {
    e.preventDefault();
    dropzone.classList.add("is-dragging");
  });

  dropzone.addEventListener("dragleave", (e) => {
    if (!dropzone.contains(e.relatedTarget)) {
      dropzone.classList.remove("is-dragging");
    }
  });

  dropzone.addEventListener("drop", (e) => {
    e.preventDefault();
    dropzone.classList.remove("is-dragging");
    input.files = e.dataTransfer.files;
    form.requestSubmit();
  });

  input.addEventListener("change", () => form.requestSubmit());
}

onViewLoad("recipes#recipe_card", () => {
  once(document.getElementById("recipe-card-sections"), "recipe-card-settings", () => {
    bindReordering();
    bindHideToggles();
    bindScaleButtons();
    bindScaleRecipeForm();
    bindImageScaleSliders();
    bindImageDropzone();
  });
});
